<script setup lang="ts">
  import { ref } from "vue";
  import { storeToRefs } from "pinia";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { TalkRoom } from "~/entities/TalkRoom";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { TALK_REPLY_TEXT_MAX } from "~/consts/TalkConfig";
  import TalkFileInput from "~/components/Organisms/TalkFileInput.vue";
  import TalkInputMessage from "~/components/Organisms/TalkInputMessage.vue";

  interface Props {
    talkRoom: TalkRoom;
    canAction: boolean;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "send"): void;
  }
  const emit = defineEmits<Emits>();

  const selectedTalkRoom = computed<TalkRoom>(() => props.talkRoom);

  const talkRoomStore = useTalkRoomStore();
  const snackBarStore = useSnackBarStore();

  const talkInputMessage = ref<InstanceType<typeof TalkInputMessage> | null>(null);
  const fileInput = ref<InstanceType<typeof TalkFileInput> | null>(null);

  // 入力データ
  const inputKey = ref(0);

  const inputLoading = ref<boolean>(false);

  // テキストメッセージ初期化
  // 初期マウント時とルーム切替時に呼び出す
  const initTextMessage = () => {
    inputKey.value++;
  };
  // ルーム切替時
  watch(selectedTalkRoom, initTextMessage);

  // 返信関連
  const { talkRoomUsers, replyTalkToEdit: replyTalk } = storeToRefs(talkRoomStore);
  watch(replyTalk, () => {
    if (replyTalk.value) {
      // 返信時はフォーカスを当てる
      if (!isIOS()) {
        // iOS はキーボード表示時のスクロールに問題があるため除外
        // https://arkad-co.slack.com/archives/C06P8P9L95G/p1736928297942039?thread_ts=1736924157.914249&cid=C06P8P9L95G
        talkInputMessage.value!.focus();
      }
    }
  });
  const replyUserName = computed<string>(() => {
    if (!replyTalk.value) {
      return "";
    }
    const user = talkRoomUsers.value[replyTalk.value.userId];
    return user?.userName || "";
  });
  const replyThumbnailImageSrc = computed(() => {
    return replyTalk.value?.thumbnailImageUrl;
  });
  watch(selectedTalkRoom, () => {
    // 返信中であればクリア
    replyTalk.value = null;
  });

  const sendFunc = async (message: string, mentionUserIds: string[]) => {
    await talkRoomStore.sendTalk(
      selectedTalkRoom.value!.talkRoomId,
      message.trim(),
      mentionUserIds,
      replyTalk.value?.talkId,
    );
    emit("send");
  };
  const onSend = () => {
    talkInputMessage.value?.triggerSend();
  };
  const onSent = () => {
    replyTalk.value = null;
  };
  const onFailed = (error: any) => {
    sentryErrorLog(`トークメッセージ送信失敗 ${error}`);
    snackBarStore.setError("送信に失敗しました");
  };

  const onClickCloseReply = () => {
    replyTalk.value = null;
  };

  const onDrop = (fileList: FileList) => {
    fileInput.value?.validateSendFiles(fileList);
  };

  // ファイル送信後処理
  const onSendFile = () => {
    replyTalk.value = null;
    emit("send");
  };

  // IDE ワーニング除去のため utils をラップ
  const sanitizeHtml = (html: string, length = Number.POSITIVE_INFINITY) => {
    return sanitizeDom(html, length);
  };

  onMounted(initTextMessage);

  defineExpose({
    onDrop,
  });
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="talkroom_inputs_wrapper d-flex flex-column">
    <!-- 返信エリア -->
    <div v-if="!!replyTalk" class="reply_area">
      <div class="close-button d-flex justify-center align-center">
        <v-icon size="24px" color="white" icon="$mdiClose" @click="onClickCloseReply"></v-icon>
      </div>
      <div class="reply_header">以下のメッセージに対して返信を行います</div>
      <div class="reply_content">
        <p class="reply_username">
          <v-icon icon="$mdiShare" size="large" class="reply-icon reverse"></v-icon>
          @{{ replyUserName }}
        </p>

        <div v-if="replyTalk.messageType === 'image'" class="reply_content_image">
          <img :src="replyThumbnailImageSrc" height="128" alt="thumbnail" />
        </div>
        <div
          v-else-if="replyTalk.messageType === 'pdf'"
          class="reply_content_text"
          v-html="sanitizeHtml(replyTalk.fileName, TALK_REPLY_TEXT_MAX)"
        ></div>
        <div v-else class="reply_content_text" v-html="sanitizeHtml(replyTalk.contents, TALK_REPLY_TEXT_MAX)"></div>

        <div v-if="replyTalk.note" class="reply_content_text" v-html="sanitizeHtml(replyTalk.note)"></div>
      </div>
    </div>

    <!-- 入力エリア -->
    <div class="talkroom_inputs">
      <div v-if="!canAction" class="can_not_action">このトークには投稿できません</div>
      <!-- ファイル添付 -->
      <OrganismsTalkFileInput
        ref="fileInput"
        :disabled="inputLoading"
        :talk-room-id="selectedTalkRoom.talkRoomId"
        :reply-talk-id="replyTalk?.talkId"
        thread-talk-id="0"
        :show-in-room="true"
        @send="onSendFile"
      />

      <!-- テキスト入力 -->
      <OrganismsTalkInputMessage
        :key="inputKey"
        ref="talkInputMessage"
        v-model:is-sending="inputLoading"
        class="talkroom_input_text"
        :talk-room-id-to-save="selectedTalkRoom.talkRoomId"
        :allow-empty-send="false"
        placeholder="入力..."
        :fixed-position="false"
        :with-send-button-margin="true"
        :send-func="sendFunc"
        @sent="onSent"
        @failed="onFailed"
      />
      <!-- 送信 -->
      <div class="talkroom_input_action talkroom_input_send h-100 d-flex align-center">
        <v-sheet class="chat-send-button" color="transparent" data-testid="send-talk-message" @click="onSend">
          <AtomsSvgIcon :width="18" :height="18" :img-path="`assets/images/icon/chat-message-send.svg`" />
        </v-sheet>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .talkroom_inputs_wrapper {
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    background-color: rgba(50, 50, 50, 1);
    .close-button {
      width: 40px;
      min-width: 40px;
      height: 40px;
      min-height: 40px;
      position: absolute;
      right: 10px;
      top: 0px;
      cursor: pointer;
    }

    .reply_area {
      text-align: left;
      width: 100%;
      padding: 0px 20px;
      .reply_header {
        font-size: 14px;
        color: rgb(var(--v-theme-main-01));
        padding: 10px 0px;
      }
      .reply_content {
        font-size: 14px;
        color: white;
        padding: 10px 20px;
        background-color: rgb(var(--v-theme-monochrome-06));
        border-radius: 15px;
      }
      .reply_username {
        font-weight: 700;
        // mdiShareアイコンが文字より下にずれるので調整
        i {
          height: 23px;
          margin-bottom: 2px;
        }
      }
      .reply_content_text {
        word-break: break-all;
        white-space: break-spaces;
      }
      .reply-icon {
        transform: scaleX(-1);
      }
    }
    .talkroom_inputs {
      width: 100%;
      display: flex;
      align-items: center;
      padding: 10px 20px 10px 10px;
      .can_not_action {
        position: absolute;
        z-index: 10;
        text-align: center;
        width: 100%;
        background-color: #000000ad;
        height: 100%;
        padding-top: 19px;
        top: 0;
        left: 0;
        @include display_sp {
          font-size: 14px;
        }
      }
      .talkroom_input_action {
        width: 40px;
        min-width: 40px;
        cursor: pointer;
        > div {
          margin: 0 auto;
        }
      }
      .talkroom_input_text {
        width: 100%;
        cursor: auto;
      }
      .talkroom_input_send {
        position: absolute;
        right: 24px;
        height: 40px !important;
      }
    }
  }

  .chat-send-button {
    padding: 6px;
    cursor: pointer;
  }
  @include display_tablet {
    .talkroom_inputs_wrapper {
      left: 0px;
      width: 100vw;
      padding-bottom: env(safe-area-inset-bottom);

      .reply_area {
        .reply_header {
          font-size: 12px;
        }
        .reply_content {
          font-size: 12px;
        }
        .reply_content_text {
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
      }
    }
  }
</style>
